import { useState, useMemo, useEffect } from 'react';
import { CheckCircle2, Clock, AlertTriangle, CreditCard, Plus, MoreHorizontal, Filter, Loader2, X, Download } from 'lucide-react';
import { useAppContext } from '../context/useAppContext';
import { formatCurrency, formatDate } from '../utils/formatters';
import { supabase } from '../lib/supabase';
import EmptyState from '../components/EmptyState';

export default function PaymentsPage() {
    const { user } = useAppContext();
    const [payments, setPayments] = useState<any[]>([]);
    const [tenantLinks, setTenantLinks] = useState<any[]>([]);
    const [properties, setProperties] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    // Filters
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterProp, setFilterProp] = useState('all');
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

    // Add/Edit payment modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPayment, setEditingPayment] = useState<any | null>(null);
    const [modalTenantLinkId, setModalTenantLinkId] = useState('');
    const [modalAmount, setModalAmount] = useState('');
    const [modalDueDate, setModalDueDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [modalStatus, setModalStatus] = useState<'pending' | 'paid'>('pending');
    const [modalPeriod, setModalPeriod] = useState(() => new Date().toISOString().slice(0, 7) + '-01');
    const [modalNotes, setModalNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const fetchData = async () => {
        if (!user.id) return;
        setLoading(true);
        try {
            const { data: props } = await supabase
                .from('properties')
                .select('*')
                .eq('owner_id', user.id);
            setProperties(props || []);
            const propIds = (props || []).map((p: any) => p.id);
            if (propIds.length > 0) {
                const [{ data: linkData }, { data: payData }] = await Promise.all([
                    supabase.from('tenant_links').select('*').in('property_id', propIds),
                    supabase.from('rent_payments').select('*').in('property_id', propIds).order('due_date', { ascending: false }),
                ]);
                setTenantLinks(linkData || []);
                setPayments(payData || []);
            }
        } catch (err) {
            console.error('Error fetching payments:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [user.id]);

    const filteredPayments = useMemo(() => payments.filter(pay => {
        const matchStatus = filterStatus === 'all' || pay.status === filterStatus;
        const matchProp = filterProp === 'all' || pay.property_id === filterProp;
        return matchStatus && matchProp;
    }), [payments, filterStatus, filterProp]);

    const stats = useMemo(() => {
        const paid = payments.filter(p => p.status === 'paid').reduce((s, p) => s + Number(p.amount), 0);
        const outstanding = payments.filter(p => p.status !== 'paid').reduce((s, p) => s + Number(p.amount), 0);
        const overdue = payments.filter(p => p.status === 'overdue').length;
        return { paid, outstanding, overdue };
    }, [payments]);

    const handleMarkPaid = async (id: string) => {
        const today = new Date().toISOString().slice(0, 10);
        const { error } = await supabase
            .from('rent_payments')
            .update({ status: 'paid', paid_date: today })
            .eq('id', id);
        if (!error) setPayments(prev => prev.map(p => p.id === id ? { ...p, status: 'paid', paid_date: today } : p));
        setActiveDropdown(null);
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Delete this payment record?')) return;
        const { error } = await supabase.from('rent_payments').delete().eq('id', id);
        if (!error) setPayments(prev => prev.filter(p => p.id !== id));
        setActiveDropdown(null);
    };

    const openAddModal = () => {
        setEditingPayment(null);
        setModalTenantLinkId('');
        setModalAmount('');
        setModalDueDate(new Date().toISOString().slice(0, 10));
        setModalStatus('pending');
        setModalPeriod(new Date().toISOString().slice(0, 7) + '-01');
        setModalNotes('');
        setIsModalOpen(true);
    };

    const openEditModal = (pay: any) => {
        setEditingPayment(pay);
        setModalTenantLinkId(pay.tenant_link_id || '');
        setModalAmount(String(pay.amount || ''));
        setModalDueDate(pay.due_date || new Date().toISOString().slice(0, 10));
        setModalStatus(pay.status === 'paid' ? 'paid' : 'pending');
        setModalPeriod(pay.period_month || new Date().toISOString().slice(0, 7) + '-01');
        setModalNotes(pay.notes || '');
        setIsModalOpen(true);
        setActiveDropdown(null);
    };

    const handleSave = async () => {
        if (!modalAmount || !modalDueDate) return;
        setIsSaving(true);
        try {
            const link = tenantLinks.find(t => t.id === modalTenantLinkId);
            const propertyId = link?.property_id || properties[0]?.id;
            const payload: any = {
                tenant_link_id: modalTenantLinkId || null,
                property_id: propertyId,
                amount: parseFloat(modalAmount),
                due_date: modalDueDate,
                status: modalStatus,
                period_month: modalPeriod || null,
                notes: modalNotes || null,
                paid_date: modalStatus === 'paid' ? new Date().toISOString().slice(0, 10) : null,
            };
            if (editingPayment) {
                const { error } = await supabase.from('rent_payments').update(payload).eq('id', editingPayment.id);
                if (error) throw error;
            } else {
                const { error } = await supabase.from('rent_payments').insert(payload);
                if (error) throw error;
            }
            setIsModalOpen(false);
            await fetchData();
        } catch (err: any) {
            alert('Error saving payment: ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const exportCsv = () => {
        const rows = [['Period', 'Tenant', 'Property', 'Amount', 'Due Date', 'Paid Date', 'Status', 'Notes']];
        filteredPayments.forEach(p => {
            const link = tenantLinks.find(t => t.id === p.tenant_link_id || t.property_id === p.property_id);
            const prop = properties.find(pr => pr.id === p.property_id);
            rows.push([
                p.period_month || '',
                link?.tenant_name || link?.tenant_email || '',
                prop?.name || '',
                String(p.amount || ''),
                p.due_date || '',
                p.paid_date || '',
                p.status || '',
                p.notes || '',
            ]);
        });
        const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        a.download = `payments-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
    };

    return (
        <div className="page" onClick={() => setActiveDropdown(null)}>
            <div className="page__header">
                <div>
                    <h1 className="page__title">Payments</h1>
                    <p className="page__subtitle">All rent payment records across your portfolio</p>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                    {payments.length > 0 && (
                        <button className="btn btn-secondary" onClick={exportCsv} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <Download size={15} /> Export CSV
                        </button>
                    )}
                    <button className="btn btn-primary" onClick={openAddModal} style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        <Plus size={16} /> Record Payment
                    </button>
                </div>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
                    <Loader2 className="animate-spin" size={48} color="var(--color-primary)" />
                </div>
            ) : payments.length === 0 ? (
                <EmptyState
                    icon={<CreditCard />}
                    title="No payments recorded"
                    description="Record rent payments manually or they'll be generated automatically each month."
                    actionLabel="Record Payment"
                    onAction={openAddModal}
                />
            ) : (
                <>
                    {/* KPI row */}
                    <div className="overview-grid" style={{ marginBottom: '1.5rem' }}>
                        {[
                            { label: 'Total Collected', value: formatCurrency(stats.paid), color: '#16a34a', icon: <CheckCircle2 size={18} /> },
                            { label: 'Outstanding', value: formatCurrency(stats.outstanding), color: '#d97706', icon: <Clock size={18} /> },
                            { label: 'Overdue Records', value: String(stats.overdue), color: stats.overdue > 0 ? '#dc2626' : '#64748b', icon: <AlertTriangle size={18} /> },
                        ].map(c => (
                            <div key={c.label} className="overview-card" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                                <span style={{ color: c.color }}>{c.icon}</span>
                                <div>
                                    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{c.label}</div>
                                    <div style={{ fontSize: '1.5rem', fontWeight: 900, color: c.color, letterSpacing: '-0.03em' }}>{c.value}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Filters */}
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', padding: '0.5rem 0.875rem', borderRadius: '10px', border: '1px solid var(--color-border)' }}>
                            <Filter size={14} color="var(--color-text-tertiary)" />
                            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ border: 'none', background: 'none', fontSize: '0.875rem', outline: 'none', cursor: 'pointer' }}>
                                <option value="all">All Statuses</option>
                                <option value="paid">Paid</option>
                                <option value="pending">Pending</option>
                                <option value="overdue">Overdue</option>
                            </select>
                        </div>
                        {properties.length > 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#fff', padding: '0.5rem 0.875rem', borderRadius: '10px', border: '1px solid var(--color-border)' }}>
                                <select value={filterProp} onChange={e => setFilterProp(e.target.value)} style={{ border: 'none', background: 'none', fontSize: '0.875rem', outline: 'none', cursor: 'pointer' }}>
                                    <option value="all">All Properties</option>
                                    {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                </select>
                            </div>
                        )}
                    </div>

                    <div className="data-panel">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Period</th>
                                    <th>Tenant</th>
                                    <th>Property</th>
                                    <th>Amount</th>
                                    <th>Due Date</th>
                                    <th>Status</th>
                                    <th>Paid Date</th>
                                    <th style={{ width: '40px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredPayments.map((pay) => {
                                    const link = tenantLinks.find(t => t.id === pay.tenant_link_id || (!pay.tenant_link_id && t.property_id === pay.property_id));
                                    const property = properties.find(p => p.id === pay.property_id);
                                    const tenantName = link?.tenant_name || link?.tenant_email || '—';
                                    const period = pay.period_month ? pay.period_month.slice(0, 7) : '—';
                                    return (
                                        <tr key={pay.id}>
                                            <td style={{ fontWeight: 600, color: 'var(--color-text-secondary)' }}>{period}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div className="avatar avatar--indigo" style={{ width: 28, height: 28, fontSize: '0.75rem' }}>{tenantName[0]?.toUpperCase()}</div>
                                                    <span>{tenantName}</span>
                                                </div>
                                            </td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>{property?.name || '—'}</td>
                                            <td style={{ fontWeight: 700 }}>{formatCurrency(pay.amount)}</td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>{pay.due_date ? formatDate(pay.due_date) : '—'}</td>
                                            <td>
                                                {pay.status === 'paid' && <span className="badge badge--green"><CheckCircle2 size={11} /> Paid</span>}
                                                {pay.status === 'pending' && <span className="badge badge--yellow"><Clock size={11} /> Pending</span>}
                                                {pay.status === 'overdue' && <span className="badge badge--red"><AlertTriangle size={11} /> Overdue</span>}
                                            </td>
                                            <td style={{ color: 'var(--color-text-secondary)' }}>{pay.paid_date ? formatDate(pay.paid_date) : '—'}</td>
                                            <td style={{ position: 'relative' }}>
                                                <button
                                                    className="btn"
                                                    style={{ padding: '4px', background: 'none', border: 'none', cursor: 'pointer' }}
                                                    onClick={e => { e.stopPropagation(); setActiveDropdown(activeDropdown === pay.id ? null : pay.id); }}
                                                >
                                                    <MoreHorizontal size={16} color="var(--color-text-tertiary)" />
                                                </button>
                                                {activeDropdown === pay.id && (
                                                    <div className="dropdown-menu" style={{ display: 'block', top: '100%', right: 0, minWidth: 150 }}>
                                                        {pay.status !== 'paid' && (
                                                            <button className="dropdown-item" onClick={() => handleMarkPaid(pay.id)}>✓ Mark as paid</button>
                                                        )}
                                                        <button className="dropdown-item" onClick={() => openEditModal(pay)}>Edit payment</button>
                                                        <button className="dropdown-item" style={{ color: '#dc2626' }} onClick={() => handleDelete(pay.id)}>Delete</button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {filteredPayments.length === 0 && (
                                    <tr>
                                        <td colSpan={8} style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
                                            No payments match the selected filters.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Add / Edit Payment Modal */}
            {isModalOpen && (
                <div className="dashboard-modal-shell" onClick={() => setIsModalOpen(false)}>
                    <div className="dashboard-modal dashboard-modal--refined" onClick={e => e.stopPropagation()} style={{ maxWidth: 520, width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h2 style={{ margin: 0, fontWeight: 800, fontSize: '1.25rem' }}>{editingPayment ? 'Edit Payment' : 'Record Payment'}</h2>
                            <button onClick={() => setIsModalOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><X size={20} /></button>
                        </div>

                        <div className="dashboard-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            {/* Tenant */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label className="auth-field">Tenant</label>
                                <select
                                    value={modalTenantLinkId}
                                    onChange={e => {
                                        setModalTenantLinkId(e.target.value);
                                        const link = tenantLinks.find(t => t.id === e.target.value);
                                        const prop = properties.find(p => p.id === link?.property_id);
                                        if (prop && !modalAmount) setModalAmount(String(prop.rent_amount || ''));
                                    }}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--color-border)', fontSize: '0.9375rem' }}
                                >
                                    <option value="">— Select tenant —</option>
                                    {tenantLinks.map(t => {
                                        const prop = properties.find(p => p.id === t.property_id);
                                        return <option key={t.id} value={t.id}>{t.tenant_name || t.tenant_email} ({prop?.name || 'Unknown'})</option>;
                                    })}
                                </select>
                            </div>

                            {/* Amount */}
                            <div>
                                <label className="auth-field">Amount (€)</label>
                                <input
                                    type="number" min="0" step="0.01"
                                    value={modalAmount}
                                    onChange={e => setModalAmount(e.target.value)}
                                    placeholder="1200"
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--color-border)', fontSize: '0.9375rem', boxSizing: 'border-box' }}
                                />
                            </div>

                            {/* Due Date */}
                            <div>
                                <label className="auth-field">Due Date</label>
                                <input
                                    type="date"
                                    value={modalDueDate}
                                    onChange={e => setModalDueDate(e.target.value)}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--color-border)', fontSize: '0.9375rem', boxSizing: 'border-box' }}
                                />
                            </div>

                            {/* Period */}
                            <div>
                                <label className="auth-field">Period (month)</label>
                                <input
                                    type="month"
                                    value={modalPeriod?.slice(0, 7)}
                                    onChange={e => setModalPeriod(e.target.value + '-01')}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--color-border)', fontSize: '0.9375rem', boxSizing: 'border-box' }}
                                />
                            </div>

                            {/* Status */}
                            <div>
                                <label className="auth-field">Status</label>
                                <select
                                    value={modalStatus}
                                    onChange={e => setModalStatus(e.target.value as 'pending' | 'paid')}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--color-border)', fontSize: '0.9375rem' }}
                                >
                                    <option value="pending">Pending</option>
                                    <option value="paid">Paid</option>
                                </select>
                            </div>

                            {/* Notes */}
                            <div style={{ gridColumn: '1 / -1' }}>
                                <label className="auth-field">Notes (optional)</label>
                                <textarea
                                    value={modalNotes}
                                    onChange={e => setModalNotes(e.target.value)}
                                    placeholder="e.g. Paid via bank transfer"
                                    rows={2}
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '10px', border: '1px solid var(--color-border)', fontSize: '0.9375rem', resize: 'vertical', boxSizing: 'border-box' }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                            <button className="btn btn-secondary" onClick={() => setIsModalOpen(false)} style={{ padding: '0.75rem 1.5rem', borderRadius: '10px', fontWeight: 700 }}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSave} disabled={isSaving || !modalAmount || !modalDueDate}
                                style={{ padding: '0.75rem 1.5rem', borderRadius: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '7px' }}>
                                {isSaving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : editingPayment ? 'Save Changes' : 'Record Payment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
